import { Link, useRouter } from "@tanstack/react-router";

/** Shared 404 view; wrapped by DefaultNotFound for router/route slots. */
export function NotFoundView({ title, message }: { title?: string; message?: string }) {
  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center">
      <p className="text-sm font-semibold text-primary">404</p>
      <h1 className="mt-2 text-2xl sm:text-3xl font-bold">{title ?? "We couldn't find that page"}</h1>
      <p className="aeo-answer mt-3 text-muted-foreground">
        {message ??
          "This page doesn't exist on Pal Stars. It may have been renamed, or the link that brought you here is out of date."}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2 text-sm">
        <Link to="/" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground hover:brightness-110">
          Home
        </Link>
        <Link to="/pals" className="rounded-md border px-4 py-2 font-medium hover:bg-card">Pal database</Link>
        <Link to="/cards" className="rounded-md border px-4 py-2 font-medium hover:bg-card">TCG cards</Link>
        <Link to="/guides" className="rounded-md border px-4 py-2 font-medium hover:bg-card">Guides</Link>
      </div>
    </div>
  );
}

/** Shared error fallback with loader-safe retry (invalidate + reset). */
export function DefaultRouteError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  if (typeof console !== "undefined") console.error(error);
  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center">
      <h1 className="text-2xl sm:text-3xl font-bold">This page didn't load</h1>
      <p className="mt-3 text-muted-foreground">
        Something went wrong while loading this section. Retry, or head somewhere else on the wiki.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2 text-sm">
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground hover:brightness-110"
        >
          Try again
        </button>
        <Link to="/" className="rounded-md border px-4 py-2 font-medium hover:bg-card">Go home</Link>
      </div>
    </div>
  );
}

/** Router-wide default 404 component (takes no meaningful props). */
export function DefaultNotFound() {
  return <NotFoundView />;
}
